import React, { useState } from "react";
import {
  startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths,
  getDay, isBefore, startOfDay, isSameDay, parseISO, isWithinInterval,
} from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

const weekDays = ["ma", "di", "wo", "do", "vr", "za", "zo"];

const AvailabilityCalendar = ({
  bookedDates = [],
  selectable = false,
  selectedDate = null,
  onSelect,
  // range mode
  mode = "single",
  rangeStart = null,
  rangeEnd = null,
  onRangeSelect,
}) => {
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState(startOfMonth(rangeStart ? parseISO(rangeStart) : today));

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const leadingBlanks = (getDay(monthStart) + 6) % 7; // Monday-first

  const isBooked = (d) => bookedDates.includes(format(d, "yyyy-MM-dd"));
  const isPast = (d) => isBefore(d, today);

  const rStart = rangeStart ? parseISO(rangeStart) : null;
  const rEnd = rangeEnd ? parseISO(rangeEnd) : null;

  const inRange = (d) => {
    if (mode !== "range" || !rStart) return false;
    if (rEnd) return isWithinInterval(d, { start: rStart, end: rEnd });
    return isSameDay(d, rStart);
  };
  const isEndpoint = (d) => (rStart && isSameDay(d, rStart)) || (rEnd && isSameDay(d, rEnd));

  const dayClass = (d) => {
    if (isPast(d)) return "bg-slate-100 text-slate-300 cursor-default";
    if (isBooked(d)) return "bg-brand-pink text-white cursor-not-allowed";
    if (mode === "range") {
      if (isEndpoint(d)) return "bg-brand-blue text-white ring-2 ring-brand-blue ring-offset-2";
      if (inRange(d)) return "bg-brand-blue/25 text-brand-navy font-bold";
      return `bg-brand-greenlight text-brand-green font-bold ${selectable ? "hover:bg-brand-green hover:text-white cursor-pointer" : ""}`;
    }
    const sel = selectedDate && isSameDay(d, parseISO(selectedDate));
    if (sel) return "bg-brand-blue text-white ring-2 ring-brand-blue ring-offset-2";
    return `bg-brand-greenlight text-brand-green font-bold ${selectable ? "hover:bg-brand-green hover:text-white cursor-pointer" : ""}`;
  };

  // Is there any booked/past day strictly needed for a valid range start..d?
  const rangeHasBlocked = (start, end) => {
    const s = start <= end ? start : end;
    const e = start <= end ? end : start;
    const span = eachDayOfInterval({ start: s, end: e });
    return span.some((day) => isBooked(day) || isPast(day));
  };

  const handleClick = (d) => {
    if (!selectable || isPast(d) || isBooked(d)) return;
    if (mode === "range") {
      if (!onRangeSelect) return;
      const iso = format(d, "yyyy-MM-dd");
      // No start yet, or a full range already picked -> begin new selection
      if (!rStart || (rStart && rEnd)) {
        onRangeSelect(iso, null);
        return;
      }
      // Have a start, picking the end
      if (isBefore(d, rStart)) {
        onRangeSelect(iso, null); // clicked earlier -> restart from here
        return;
      }
      if (isSameDay(d, rStart)) {
        onRangeSelect(iso, iso); // single day
        return;
      }
      if (rangeHasBlocked(rStart, d)) {
        // Blocked day in between -> restart selection at the new click
        onRangeSelect(iso, null);
        return;
      }
      onRangeSelect(format(rStart, "yyyy-MM-dd"), iso);
      return;
    }
    if (!onSelect) return;
    onSelect(format(d, "yyyy-MM-dd"));
  };

  return (
    <div className="max-w-sm">
      <div className="flex items-center justify-between mb-4">
        <p className="font-display text-brand-blue capitalize text-base sm:text-lg">
          {format(cursor, "LLLL yyyy", { locale: nl })}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCursor(addMonths(cursor, -1))}
            disabled={isBefore(addMonths(cursor, -1), startOfMonth(today))}
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-brand-navy hover:bg-brand-bluelight disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-brand-navy hover:bg-brand-bluelight transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
        {weekDays.map((w) => (
          <div key={w} className="text-center text-[10px] sm:text-xs font-bold uppercase text-slate-400 py-0.5">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {Array.from({ length: leadingBlanks }).map((_, i) => <div key={`b${i}`} />)}
        {days.map((d) => (
          <button
            type="button"
            key={d.toISOString()}
            onClick={() => handleClick(d)}
            data-testid={selectable && !isPast(d) && !isBooked(d) ? `cal-day-${format(d, "yyyy-MM-dd")}` : undefined}
            className={`aspect-square rounded-lg sm:rounded-xl flex items-center justify-center text-xs sm:text-sm transition-all ${dayClass(d)}`}
          >
            {format(d, "d")}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-5 text-xs sm:text-sm">
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-brand-greenlight border border-brand-green/30" /> Beschikbaar</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-brand-pink" /> Bezet</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-slate-100 border border-slate-200" /> Verlopen</span>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
